import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import { captureError } from '@ben/api/capture-error';
import StylexTracer from '@ben/orion/stylex-tracer';
import { ModalProvider } from '@ben/orion/utils/confirm-action';
import ThemeProvider from '@ben/shared/components/theme-provider';
import { OrionMantineProvider } from '@ben/orion/utils/orion-mantine-provider';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as ReactDOMClient from 'react-dom/client';
import { BrowserRouter, HashRouter, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import App from './app/app';
import AssistantWidget from './components/assistant/assistant-widget';

// Use HashRouter for branch previews
const isBranchPreview = window.location.pathname.startsWith('/modben/testing/');
const Router = isBranchPreview ? HashRouter : BrowserRouter;

const DEVELOPMENT_ENVIRONMENT = 'development';

const deploymentEnvironment =
  process.env.REACT_APP_DEPLOYMENT_ENVIRONMENT || DEVELOPMENT_ENVIRONMENT;
const sentryUrl = process.env.REACT_APP_SENTRY_URL || '';
const audienceUrl = process.env.REACT_APP_AUDIENCE_URL || '';
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
const BASE_URL = isBranchPreview ? '' : process.env.REACT_APP_BASE_URL;

Sentry.init({
  enabled: deploymentEnvironment !== DEVELOPMENT_ENVIRONMENT,
  dsn: `${sentryUrl}`,
  environment: deploymentEnvironment,
  maxValueLength: 500,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  beforeSend(event: any, hint) {
    const error = hint?.originalException;
    const errorMessage = (error as { message?: string })?.message;

    if (
      errorMessage?.includes('status code 401') ||
      errorMessage?.includes('status code 400') ||
      errorMessage?.includes('No QueryClient')
    ) {
      return null;
    }

    return event;
  },
});

const redirectUri = BASE_URL
  ? `${window.location.origin}${BASE_URL}/`
  : window.location.origin;

const container = document.querySelector('#root');
const root = container && ReactDOMClient.createRoot(container);

const mutationCache = new MutationCache({
  onError: (error) => {
    captureError(error);
  },
});

const queryCache = new QueryCache({
  onError: (error) => {
    captureError(error);
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60,
    },
  },
  mutationCache,
  queryCache,
});

const WrappedApp = () => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(
      {
        pathname:
          (appState?.['targetUrl'] as string | undefined) ??
          window.location.pathname,
        search: undefined,
      },
      { replace: true }
    );
  };

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={clientId}
      authorizationParams={{
        // eslint-disable-next-line camelcase
        redirect_uri: redirectUri,
        audience: audienceUrl,
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <OrionMantineProvider>
            <ModalProvider>
              <App />
              <ToastContainer
                position="bottom-left"
                autoClose={5000}
                closeButton={false}
                className="toast-notification"
              />
              <AssistantWidget />
            </ModalProvider>
          </OrionMantineProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Auth0Provider>
  );
};

root?.render(
  <>
    <StylexTracer />
    <Router basename={BASE_URL || ''}>
      <WrappedApp />
    </Router>
  </>
);
